import React, { useCallback, useContext, useEffect, useState } from "react";

import {
  Alert,
  CircularProgress,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
} from "@mui/material";
import clsx from "clsx";
import { useParams } from "react-router-dom";
import mainStyles from "styles";

import {
  Component,
  ComponentPrice,
} from "@safetyculture/s12-apis-connect-web/s12/subscriptions/v1/components_pb";
import { Currency } from "@safetyculture/s12-apis-connect-web/s12/tools/support/v1/support_pb";

import { getCurrencyFromString } from "../../../../helpers/get-currency-from-string";
import ComponentPriceInfo from "./../../../shared/component-price-info";
import { getBillingComponents } from "api/endpoints/components";
import { callOptionsHeaders } from "api/helpers";
import { currencyMapping, pricingPeriodToMonthsMapping } from "constants/index";
import { SnackbarContext } from "context/snackbar-context";
import {
  getBillingFrequency,
  getSubscriptionCurrency,
} from "helpers/subscriptions";
import {
  ComponentPricePickerWrapperProps,
  UrlParamTypes,
} from "types/org-display";

const ComponentPricePicker = ({
  subscription,
  currentPricePoint,
  componentId,
  onComponentPriceChange,
}: ComponentPricePickerWrapperProps) => {
  const { regionCode } = useParams<UrlParamTypes>();
  const classes = mainStyles();
  const [isLoading, setIsLoading] = useState(false);
  const [pricePointOptions, setPricePointOptions] =
    useState<ComponentPrice[]>();
  const [newPrice, setNewPrice] = useState<ComponentPrice | undefined>();
  const currency = getSubscriptionCurrency(subscription);
  const billingFrequency = getBillingFrequency(subscription);
  const subCurrency: Currency | undefined = currency
    ? currencyMapping[currency as keyof typeof currencyMapping]
    : undefined;

  const { showError } = useContext(SnackbarContext);

  const handleComponentPricePointChange = (event: SelectChangeEvent) => {
    const pricePointId = event.target.value as string;
    const newComponentPrice = pricePointOptions?.find(
      (e) => e.id.toString() === pricePointId,
    );
    if (newComponentPrice) {
      setNewPrice(newComponentPrice);
      onComponentPriceChange(newComponentPrice);
    }
  };

  const fetchBillingComponents = useCallback(() => {
    const filterOptions = (
      billingComponentsList: Component[],
      componentId: number,
      subCurrency: Currency | undefined,
    ) => {
      const component = billingComponentsList.find((e) => e.id === componentId);
      const options = component?.componentPrice;
      if (subCurrency === undefined) {
        return options;
      } else {
        const optionsWithCurrencyFilter = options?.filter(
          (e) =>
            getCurrencyFromString(e.currency) === subCurrency &&
            e.period === pricingPeriodToMonthsMapping[billingFrequency],
        );
        return optionsWithCurrencyFilter;
      }
    };

    setIsLoading(true);
    getBillingComponents(
      subscription.chargify?.site ?? "",
      callOptionsHeaders(regionCode, subscription.id),
    )
      .then((res) => {
        const billingComponentsList = res?.components;
        setPricePointOptions(
          filterOptions(billingComponentsList, componentId, subCurrency),
        );
      })
      .catch(showError)
      .finally(() => {
        setIsLoading(false);
      });
  }, [
    componentId,
    regionCode,
    showError,
    subCurrency,
    subscription.id,
    billingFrequency,
    subscription.chargify?.site,
  ]);

  useEffect(() => {
    fetchBillingComponents();
  }, [fetchBillingComponents]);

  return (
    <>
      {isLoading ? (
        <div style={{ textAlign: "center" }} data-testid="loader">
          <CircularProgress />
        </div>
      ) : (
        <div
          data-testid="component-price-picker-container"
          className={clsx(classes.spaceTopSmall)}
          style={{ width: "100%" }}
        >
          {!pricePointOptions || !pricePointOptions.length ? (
            <Alert severity="warning" variant="filled">
              Cannot load price options
            </Alert>
          ) : (
            <FormControl
              variant="outlined"
              size="small"
              sx={classes.formControl}
              fullWidth
              style={{ margin: "0rem 0rem 2rem 0rem" }}
            >
              <InputLabel id="select-doc-type-label">
                new component price
              </InputLabel>
              <Select
                labelId="select-doc-type-label"
                id="select-doc-type"
                value={newPrice?.id.toString()}
                label="new component price"
                onChange={(event) => handleComponentPricePointChange(event)}
                data-anchor="component-price-select"
                data-testid="component-price-select"
              >
                {pricePointOptions &&
                  pricePointOptions.map((item) => {
                    return (
                      <MenuItem
                        key={item.id.toString()}
                        value={item.id.toString()}
                        data-anchor={"component-price-option"}
                        data-testid={"component-price-option"}
                        disabled={currentPricePoint?.id === item.id}
                      >
                        <ComponentPriceInfo
                          componentPrice={item}
                          unitLabel="seat"
                        />
                      </MenuItem>
                    );
                  })}
              </Select>
            </FormControl>
          )}
        </div>
      )}
    </>
  );
};

export default ComponentPricePicker;
